// src/components/PaymentCallback.js
import React, { useEffect, useState } from 'react';
import { Container, Typography, Button, Alert } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../CartContext';
import { postJSON } from '../api';

function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

export default function PaymentCallback() {
  const query = useQuery();
  const status = query.get('status');
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const [working, setWorking] = useState(true);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    async function run() {
      if (status !== 'success') {
        setWorking(false);
        setMsg('Your payment failed, please try again.');
        return;
      }
      try {
        const raw = sessionStorage.getItem('pendingOrder');
        if (!raw) {
          setWorking(false);
          setMsg('Payment succeeded but order details were not found. Please place the order again.');
          return;
        }
        const order = JSON.parse(raw);
        const created = await postJSON('/order', order);
        const id = created?.id || 'CONFIRMED';
        sessionStorage.removeItem('pendingOrder');
        clearCart();
        navigate(`/payment-success/${id}`, { replace: true });
      } catch (e) {
        console.error(e);
        setWorking(false);
        setMsg('We recorded your payment but could not save the order. Please contact support.');
      }
    }
    run();
  }, [status, navigate, clearCart]);

  async function retryPayment() {
    try {
      const raw = sessionStorage.getItem('pendingOrder');
      if (!raw) {
        navigate('/cart');
        return;
      }
      const order = JSON.parse(raw);
      const { href } = await postJSON('/payments/create-checkout', {
        items: order.items,
        orderType: order.orderType,
        userEmail: order.userEmail || ''
      });
      window.location.assign(href);
    } catch (e) {
      console.error(e);
      setMsg('Could not restart checkout. Please try again or return to your cart.');
    }
  }

  if (working) {
    return (
      <Container sx={{ py: 6 }}>
        <Typography variant="h5">Finalizing your order…</Typography>
        <Typography>Please do not close this page.</Typography>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 6 }}>
      <Alert severity="error" sx={{ mb: 3 }}>{msg}</Alert>
      <Button variant="contained" onClick={retryPayment} sx={{ mr: 2 }}>
        Retry payment
      </Button>
      <Button variant="outlined" onClick={() => navigate('/cart')}>
        Go back to cart
      </Button>
    </Container>
  );
}
